import React, { useContext } from 'react';

import type { Image as ImageGQL, NewBuildingCard } from '@search/graphql-typings';

import classname from '@search/classname/src';

import CustomTypography from '@search/vtbeco-frontend-core/domain/newbuilding/components/common/CustomTypography';
import { DATA_GTM } from '@search/vtbeco-frontend-core/domain/newbuilding/libs/constants';
import Image from '@search/vtbeco-frontend-core/view/common/components/Image';
import { CallbackModal } from '@search/vtbeco-frontend-core/view/common/components/CallbackModal';
import { QRCodePhoneButton } from '@search/vtbeco-frontend-core/view/common/components/QRCodePhoneButton';

import { SvgSpriteIcon } from '../../../../../../client/common/components/SvgSpriteIcon';
import { defineCallbackEmailSender } from '../../../../../../client/common/libs/defineEmailSender';
import FavoritesButton from '../../../../../../client/common/components/FavoritesButton';
import { GlobalContext } from '../../../../../../client/common/components/GlobalContext';
import { usePhoneButtonContext } from '../../../../../../client/common/components/NewBuildingCard/PhoneButtonStateProvider';
import ChatButton from '../../../../../../client/common/components/ChatButton';
import { M2ProOrderButton } from '../../../../../../client/common/components/M2ProOrderButton';
import {
    M2ProAttachedFilesButton,
    M2ProAttachedFilesButtonProps
} from '../../../../../../client/common/components/M2ProAttachedFilesButton';

import './styles.css';

export interface INewBuildingCardDeveloper {
    developerLink?: string;
    developerTitle?: string;
    developerImage?: ImageGQL | null;
    developerNames: string;
    dataGTM: DATA_GTM;
    isMskMO?: boolean;
    isSPbLO?: boolean;
    isSpecProjectRegion?: boolean;
    hasChat?: boolean;
    newBuildingId?: NewBuildingCard['id'];
    newBuildingName: NewBuildingCard['name'];
    newBuildingType: NewBuildingCard['type'];
    newBuildingTitle: NewBuildingCard['title']['nominative'];
    newBuildingAddress: NewBuildingCard['address'];
    favoritesButtonProps?: React.ComponentProps<typeof FavoritesButton>;
    renderMortgageButton: () => React.ReactNode;
    hasM2Pro: boolean;
    m2ProFiles: M2ProAttachedFilesButtonProps['files'];
}

const cn = classname.bind(null, 'NewBuildingCardDeveloper');

const NewBuildingCardDeveloper: React.FunctionComponent<INewBuildingCardDeveloper> = ({
    developerLink,
    developerTitle,
    developerImage,
    developerNames,
    dataGTM,
    isMskMO,
    isSPbLO,
    isSpecProjectRegion,
    hasChat,
    newBuildingId,
    newBuildingName,
    newBuildingType,
    newBuildingTitle,
    newBuildingAddress,
    favoritesButtonProps,
    renderMortgageButton,
    hasM2Pro,
    m2ProFiles
}) => {
    const { instanceConfig: { hasFavoritesButton, calltouchUnitId } } = useContext(GlobalContext);
    const { buttonsData, handleClick, firstDeveloperId, handleStatEventPush } = usePhoneButtonContext();

    const hasCallback = isMskMO || isSPbLO;
    const hasOnlyChat = hasChat && ! hasCallback;
    const isPhoneRegion = isMskMO || isSPbLO || isSpecProjectRegion;

    const renderButtons = () => {
        if (hasM2Pro) {
            return (
                <>
                    <div className={cn('m2ProButton')}>
                        {newBuildingId ? (
                            <M2ProOrderButton
                                newBuildingId={String(newBuildingId)}
                                fullWidth
                                eventData={{
                                    page_type: 'card',
                                    position: 'bottom'
                                }}
                            />
                        ) : (
                            <ChatButton
                                variant='primary'
                                size='m'
                                fullWidth
                                hasText
                                hasIcon={false}
                            />
                        )}
                    </div>
                    {m2ProFiles.length > 0 && (
                        <div className={cn('m2ProButton')}>
                            <M2ProAttachedFilesButton
                                view='button'
                                variant='transparent'
                                files={m2ProFiles}
                                eventData={{
                                    page_type: 'card',
                                    position: 'bottom'
                                }}
                            />
                        </div>
                    )}
                </>
            );
        }

        return (<>
            {
                isPhoneRegion ? firstDeveloperId && (
                    <QRCodePhoneButton
                        href={buttonsData[firstDeveloperId].href}
                        onClick={() => handleClick(firstDeveloperId)}
                        qrCodeValue={buttonsData[firstDeveloperId].qrCodeValue}
                        className={cn('showPhone')}
                        gtm={dataGTM}
                        loading={buttonsData[firstDeveloperId].isLoading}
                        buttonVariant='primary'
                    >
                        {buttonsData[firstDeveloperId].text}
                    </QRCodePhoneButton>
                ) : renderMortgageButton()
            }
            {
                hasCallback || hasChat ? (
                    <div className={cn('feedbackButtons')}>
                        {hasCallback ? (
                            <CallbackModal
                                newBuildingName={newBuildingTitle}
                                address={newBuildingAddress}
                                emailSender={defineCallbackEmailSender(isMskMO, isSPbLO)}
                                developerName={developerNames}
                                gtmSendClick={DATA_GTM.CARD_CALL_BACK_SEND}
                                onSendEventPush={handleStatEventPush}
                                gtmShowModal={DATA_GTM.CARD_CALL_BACK}
                                btnWrapperClass={cn('callBackButton')}
                                callbackUnitId={calltouchUnitId[isSPbLO ? 'Spb' : 'Msk']}
                                withoutIconOnButton
                            />
                        ) : null}
                        {hasChat ? (
                            <ChatButton
                                className={cn('openChat', { alone: hasOnlyChat })}
                                size='m'
                                fullWidth={hasOnlyChat}
                                hasText={hasOnlyChat}
                            />
                        ) : null}
                    </div>
                ) : null
            }
        </>);
    };

    return (
        <div className={cn()}>
            <div className={cn('top')}>
                <div>
                    <CustomTypography
                        level='primary-alone'
                        color='secondary'
                    >
                        {newBuildingType}
                    </CustomTypography>
                    <CustomTypography
                        bold
                        level='primary-alone'
                        className={cn('newBuildingName')}
                    >
                        {newBuildingName}
                    </CustomTypography>
                </div>
                {
                    hasFavoritesButton ? (
                        <FavoritesButton
                            {...favoritesButtonProps}
                            inCircle
                        />
                    ) : null
                }
            </div>
            {renderButtons()}
            <div className={cn('developer')}>
                <div>
                    <CustomTypography
                        level='primary-alone'
                        color='secondary'
                    >
                        Застройщик
                    </CustomTypography>
                    <CustomTypography
                        bold
                        tag='a'
                        level='primary-alone'
                        color='primary'
                        target='_blank'
                        href={developerLink}
                        className={cn('developerName')}
                    >
                        {developerTitle}
                    </CustomTypography>
                </div>
                <div className={cn('developerImage')}>
                    {developerImage?.origin ? (
                        <Image
                            type='contain'
                            width={40}
                            height={40}
                            url={developerImage.origin}
                        />
                    ) : (
                        <SvgSpriteIcon
                            width={40}
                            height={40}
                            name='CustomDeveloper'
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(NewBuildingCardDeveloper);
